<!-- SharedSoftware.vue - 共享软件卡片组件（五行展示） -->
<template>
	<div class="shared-software-page">
		<div class="page-header">
			<h1>📦 共享软件资源库</h1>
			<p>点击卡片即可访问网页或下载软件</p>
		</div>

		<!-- 搜索和筛选栏 -->
		<div class="filter-bar">
			<div class="search-wrapper">
				<span class="search-icon">🔍</span>
				<input type="text" v-model="searchKeyword" placeholder="搜索软件名称..." class="search-input"
					@input="handleSearch" />
				<button v-if="searchKeyword" class="clear-btn" @click="clearSearch">✕</button>
			</div>

			<div class="category-filter">
				<button v-for="cat in categories" :key="cat.value"
					:class="['category-btn', { active: currentCategory === cat.value }]"
					@click="filterByCategory(cat.value)">
					{{ cat.label }}
				</button>
			</div>
		</div>

		<!-- 统计信息 -->
		<div class="stats-info" v-if="filteredSoftware.length > 0">
			共找到 <strong>{{ filteredSoftware.length }}</strong> 个软件资源
		</div>

		<!-- 软件卡片网格 - 每行5列 -->
		<div class="software-grid" v-if="!loading">
			<div class="software-card" v-for="item in paginatedSoftware" :key="item.id" @click="handleCardClick(item)">
				<div class="card-icon">{{ item.icon || getRandomIcon() }}</div>
				<h3 class="card-title">{{ item.name }}</h3>
				<p class="card-desc">{{ item.desc }}</p>
				<div class="card-type-badge"
					:class="{ 'url-badge': item.type === 'url', 'file-badge': item.type === 'file' }">
					{{ item.type === 'url' ? '🌐 网页链接' : '📁 下载文件' }}
				</div>
				<div class="card-path" :title="item.path">
					{{ truncatePath(item.path) }}
				</div>
				<div class="card-hint">
					{{ item.type === 'url' ? '点击跳转网页 →' : '点击下载文件 →' }}
				</div>
			</div>

			<!-- 空状态 -->
			<div class="empty-state" v-if="filteredSoftware.length === 0">
				<span class="empty-icon">🔍</span>
				<p>没有找到与“<strong>{{ searchKeyword }}</strong>”相关的软件</p>
				<button class="reset-btn" @click="clearSearch">查看全部</button>
			</div>
		</div>

		<!-- 加载状态 -->
		<div class="loading-state" v-if="loading">
			<div class="loading-spinner"></div>
			<p>加载中...</p>
		</div>

		<!-- 分页组件 -->
		<div class="pagination-wrapper" v-if="totalPages > 1 && filteredSoftware.length > 0">
			<button class="page-btn" :disabled="currentPage === 1" @click="goToPage(1)">⏮</button>
			<button class="page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">◀</button>

			<div class="page-numbers">
				<button v-for="page in displayedPages" :key="page"
					:class="['page-num', { active: currentPage === page, dots: page === '...' }]"
					:disabled="page === '...'" @click="page !== '...' && goToPage(page)">
					{{ page }}
				</button>
			</div>

			<button class="page-btn" :disabled="currentPage === totalPages"
				@click="goToPage(currentPage + 1)">▶</button>
			<button class="page-btn" :disabled="currentPage === totalPages" @click="goToPage(totalPages)">⏭</button>

			<div class="page-info">
				第 {{ currentPage }} / {{ totalPages }} 页
			</div>
		</div>
	</div>
</template>

<script>
	// 从外部文件引入软件数据
	import softwareListAll from '../views/dataView/softwareListAll.js';
	
	export default {
		name: 'SharedSoftware',
		data() {
			return {
				loading: false,
				searchKeyword: '',
				currentCategory: 'all',
				currentPage: 1,
				pageSize: 10, // 每页10个，即2行（每行5个）
				categories: [
					{ value: 'all', label: '全部' },
					{ value: 'url', label: '🌐 网页链接' },
					{ value: 'file', label: '📁 文件下载' }
				],
				// 软件数据列表（从外部引入）
				softwareList: [],
				iconLibrary: [
					'📘', '🐙', '💬', '📄', '📦', '🔧', '✏️', '🎨', '⛏️', '💻',
					'🤔', '📺', '📝', '🔀', '🟢', '🌐', '📮', '🐳', '🗂️', '🗄️',
					'🖥️', '🗜️', '⚙️', '🔗', '📎', '💾', '🖱️', '⌨️', '🖨️', '📡'
				]
			};
		},
		computed: {
			// 过滤后的软件列表（按分类和搜索关键词）
			filteredSoftware() {
				let result = this.softwareList;

				// 按分类过滤
				if (this.currentCategory !== 'all') {
					result = result.filter(item => item.type === this.currentCategory);
				}

				// 按搜索关键词过滤
				if (this.searchKeyword.trim()) {
					const keyword = this.searchKeyword.trim().toLowerCase();
					result = result.filter(item =>
						item.name.toLowerCase().includes(keyword) ||
						item.desc.toLowerCase().includes(keyword)
					);
				}

				return result;
			},
			// 总页数
			totalPages() {
				return Math.ceil(this.filteredSoftware.length / this.pageSize);
			},
			// 当前页数据
			paginatedSoftware() {
				const start = (this.currentPage - 1) * this.pageSize;
				const end = start + this.pageSize;
				return this.filteredSoftware.slice(start, end);
			},
			// 显示的分页按钮
			displayedPages() {
				const total = this.totalPages;
				const current = this.currentPage;
				const delta = 2;
				const range = [];

				for (let i = 1; i <= total; i++) {
					if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
						range.push(i);
					} else if (range[range.length - 1] !== '...') {
						range.push('...');
					}
				}
				return range;
			}
		},
		watch: {
			searchKeyword() {
				this.currentPage = 1;
			},
			currentCategory() {
				this.currentPage = 1;
			}
		},
		created() {
			// 组件创建时加载外部数据
			this.loadSoftwareData();
		},
		mounted() {
			this.loading = false;
		},
		methods: {
			// 加载软件数据
			loadSoftwareData() {
				// 判断导入的数据格式
				if (Array.isArray(softwareListAll)) {
					this.softwareList = softwareListAll;
				} else if (softwareListAll && softwareListAll.default && Array.isArray(softwareListAll.default)) {
					this.softwareList = softwareListAll.default;
				} else if (softwareListAll && softwareListAll.softwareList) {
					this.softwareList = softwareListAll.softwareList;
				} else {
					// 如果外部数据为空，使用默认数据
					this.softwareList = this.getDefaultSoftwareList();
				}
				console.log(`加载了 ${this.softwareList.length} 个软件资源`);
			},
			// 默认软件数据（当外部数据不存在时使用）
			getDefaultSoftwareList() {
				return [
					// 网页链接类
					// { id: 1, name: 'Vue.js 官方文档', desc: 'Vue.js 渐进式 JavaScript 框架官方文档', type: 'url', path: 'https://vuejs.org/', icon: '📘' },
					// { id: 2, name: 'GitHub 开源社区', desc: '全球最大的代码托管平台', type: 'url', path: 'https://github.com/', icon: '🐙' },
					// { id: 3, name: 'Stack Overflow', desc: '程序员问答社区', type: 'url', path: 'https://stackoverflow.com/', icon: '💬' },
					// { id: 4, name: 'MDN Web Docs', desc: 'Web 技术权威文档', type: 'url', path: 'https://developer.mozilla.org/', icon: '📄' },
					// { id: 5, name: 'NPM 包管理器', desc: 'Node.js 包管理工具官网', type: 'url', path: 'https://www.npmjs.com/', icon: '📦' },
					// { id: 6, name: 'Can I Use', desc: '浏览器兼容性查询工具', type: 'url', path: 'https://caniuse.com/', icon: '🔧' },
					// { id: 7, name: 'CodePen', desc: '前端代码在线编辑和展示', type: 'url', path: 'https://codepen.io/', icon: '✏️' },
					// { id: 8, name: 'Figma 社区', desc: 'UI 设计协作平台', type: 'url', path: 'https://www.figma.com/', icon: '🎨' },
					// { id: 9, name: '掘金技术社区', desc: '国内优质技术分享社区', type: 'url', path: 'https://juejin.cn/', icon: '⛏️' },
					// { id: 10, name: 'CSDN 开发者社区', desc: '中文技术交流平台', type: 'url', path: 'https://www.csdn.net/', icon: '💻' },
					// // 文件下载类
					// { id: 11, name: 'VSCode 安装包', desc: 'Visual Studio Code 代码编辑器', type: 'file', path: '/downloads/vscode-setup.exe', icon: '📝' },
					// { id: 12, name: 'Git 客户端', desc: '分布式版本控制工具', type: 'file', path: '/downloads/git-installer.exe', icon: '🔀' },
					// { id: 13, name: 'Node.js 运行时', desc: 'JavaScript 运行环境', type: 'file', path: '/downloads/nodejs.msi', icon: '🟢' },
					// { id: 14, name: 'Chrome 浏览器', desc: 'Google 高速浏览器', type: 'file', path: '/downloads/chrome.exe', icon: '🌐' },
					// { id: 15, name: 'Postman API 工具', desc: 'API 测试和调试工具', type: 'file', path: '/downloads/postman.exe', icon: '📮' },
					// { id: 16, name: 'Docker Desktop', desc: '容器化开发平台', type: 'file', path: '/downloads/docker.exe', icon: '🐳' },
					// { id: 17, name: 'Typora 编辑器', desc: 'Markdown 写作工具', type: 'file', path: '/downloads/typora.exe', icon: '📝' },
					// { id: 18, name: 'Obsidian 笔记', desc: '知识管理工具', type: 'file', path: '/downloads/obsidian.exe', icon: '🗂️' },
					// { id: 19, name: 'Navicat Premium', desc: '数据库管理工具', type: 'file', path: '/downloads/navicat.exe', icon: '🗄️' },
					// { id: 20, name: '7-Zip 压缩工具', desc: '开源压缩软件', type: 'file', path: '/downloads/7zip.exe', icon: '🗜️' }
				];
			},
			// 获取随机图标
			getRandomIcon() {
				const randomIndex = Math.floor(Math.random() * this.iconLibrary.length);
				return this.iconLibrary[randomIndex];
			},
			// 截断路径显示
			truncatePath(path) {
				if (!path) return '';
				if (path.length > 40) {
					return path.substring(0, 37) + '...';
				}
				return path;
			},
			// 处理卡片点击 - 核心跳转/下载逻辑
			handleCardClick(item) {
				console.log(`点击: ${item.name}, 类型: ${item.type}, 路径: ${item.path}`);

				if (!item.path) {
					if (this.$message) {
						this.$message.error('资源地址不存在');
					} else {
						alert('资源地址不存在');
					}
					return;
				}

				if (item.type === 'url') {
					// 网页链接：在新窗口打开
					window.open(item.path, '_blank');
				} else if (item.type === 'file') {
					// 文件下载：创建下载链接
					this.downloadFile(item.path, item.name);
				}
			},
			// 下载文件方法
			downloadFile(filePath, fileName) {
				try {
					// 创建一个隐藏的 <a> 标签来触发下载
					const link = document.createElement('a');
					link.style.display = 'none';
					link.href = filePath;
					link.download = fileName || this.getFileNameFromPath(filePath);

					document.body.appendChild(link);
					link.click();

					// 清理
					setTimeout(() => {
						document.body.removeChild(link);
					}, 100);

					console.log(`开始下载: ${filePath}`);
				} catch (error) {
					console.error('下载失败:', error);
					if (this.$message) {
						this.$message.error('下载失败，请检查文件路径是否正确');
					} else {
						alert('下载失败，请检查文件路径是否正确');
					}
				}
			},
			// 从路径中提取文件名
			getFileNameFromPath(path) {
				if (!path) return 'download';
				const parts = path.split('/');
				return parts[parts.length - 1] || 'download';
			},
			// 搜索处理（防抖）
			handleSearch() {
				if (this.searchTimer) {
					clearTimeout(this.searchTimer);
				}
				// 搜索逻辑已在 watch 中处理
			},
			// 清空搜索
			clearSearch() {
				this.searchKeyword = '';
			},
			// 按分类筛选
			filterByCategory(category) {
				this.currentCategory = category;
			},
			// 跳转页面
			goToPage(page) {
				if (page >= 1 && page <= this.totalPages) {
					this.currentPage = page;
					// 滚动到顶部
					window.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				}
			}
		}
	};
</script>

<style scoped>
	.shared-software-page {
		min-height: 100vh;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		padding: 2rem;
	}

	.page-header {
		text-align: center;
		margin-bottom: 2rem;
	}

	.page-header h1 {
		font-size: 2rem;
		color: white;
		margin-bottom: 0.5rem;
		text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
	}

	.page-header p {
		color: rgba(255, 255, 255, 0.9);
		font-size: 1rem;
	}

	/* 筛选栏 */
	.filter-bar {
		background: white;
		border-radius: 1rem;
		padding: 1rem 1.5rem;
		margin-bottom: 1.5rem;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}

	.search-wrapper {
		position: relative;
		margin-bottom: 1rem;
	}

	.search-icon {
		position: absolute;
		left: 1rem;
		top: 50%;
		transform: translateY(-50%);
		font-size: 1rem;
		color: #94a3b8;
	}

	.search-input {
		width: 100%;
		padding: 0.75rem 2.5rem;
		border: 1px solid #e2e8f0;
		border-radius: 0.75rem;
		font-size: 0.95rem;
		transition: all 0.2s;
	}

	.search-input:focus {
		outline: none;
		border-color: #667eea;
		box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
	}

	.clear-btn {
		position: absolute;
		right: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		background: #e2e8f0;
		border: none;
		width: 1.75rem;
		height: 1.75rem;
		border-radius: 50%;
		cursor: pointer;
		font-size: 0.8rem;
		color: #64748b;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.clear-btn:hover {
		background: #cbd5e1;
	}

	.category-filter {
		display: flex;
		gap: 0.75rem;
		flex-wrap: wrap;
	}

	.category-btn {
		padding: 0.5rem 1.25rem;
		border: 1px solid #e2e8f0;
		background: #f8fafc;
		border-radius: 2rem;
		cursor: pointer;
		font-size: 0.85rem;
		transition: all 0.2s;
	}

	.category-btn:hover {
		background: #e2e8f0;
	}

	.category-btn.active {
		background: #667eea;
		border-color: #667eea;
		color: white;
	}

	/* 统计信息 */
	.stats-info {
		text-align: center;
		margin-bottom: 1rem;
		color: white;
		font-size: 0.9rem;
	}

	.stats-info strong {
		font-size: 1.1rem;
	}

	/* 卡片网格 - 每行5列布局 */
	.software-grid {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: 1.25rem;
		margin-bottom: 2rem;
	}

	.software-card {
		background: white;
		border-radius: 1rem;
		padding: 1rem;
		cursor: pointer;
		transition: all 0.3s ease;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		position: relative;
		overflow: hidden;
	}

	.software-card:hover {
		transform: translateY(-4px);
		box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
	}

	.card-icon {
		font-size: 2rem;
		margin-bottom: 0.5rem;
	}

	.card-title {
		font-size: 0.95rem;
		font-weight: 600;
		color: #1e293b;
		margin-bottom: 0.4rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.card-desc {
		font-size: 0.7rem;
		color: #64748b;
		margin-bottom: 0.6rem;
		line-height: 1.3;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.card-type-badge {
		display: inline-block;
		padding: 0.2rem 0.6rem;
		border-radius: 2rem;
		font-size: 0.65rem;
		font-weight: 500;
		margin-bottom: 0.5rem;
	}

	.url-badge {
		background: #dbeafe;
		color: #1e40af;
	}

	.file-badge {
		background: #dcfce7;
		color: #166534;
	}

	.card-path {
		font-size: 0.65rem;
		color: #94a3b8;
		background: #f8fafc;
		padding: 0.3rem 0.5rem;
		border-radius: 0.5rem;
		margin-bottom: 0.6rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		font-family: monospace;
	}

	.card-hint {
		font-size: 0.65rem;
		color: #667eea;
		text-align: right;
		opacity: 0.7;
		transition: opacity 0.2s;
	}

	.software-card:hover .card-hint {
		opacity: 1;
	}

	/* 空状态 */
	.empty-state {
		grid-column: 1 / -1;
		text-align: center;
		padding: 3rem;
		background: white;
		border-radius: 1rem;
	}

	.empty-icon {
		font-size: 3rem;
		display: block;
		margin-bottom: 1rem;
		opacity: 0.6;
	}

	.empty-state p {
		color: #64748b;
		margin-bottom: 1rem;
	}

	.reset-btn {
		background: #667eea;
		color: white;
		border: none;
		padding: 0.5rem 1.5rem;
		border-radius: 2rem;
		cursor: pointer;
	}

	.reset-btn:hover {
		background: #5a67d8;
	}

	/* 加载状态 */
	.loading-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem;
		background: white;
		border-radius: 1rem;
	}

	.loading-spinner {
		width: 40px;
		height: 40px;
		border: 3px solid #e2e8f0;
		border-top-color: #667eea;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
		margin-bottom: 1rem;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	/* 分页 */
	.pagination-wrapper {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		flex-wrap: wrap;
		margin-top: 1.5rem;
	}

	.page-btn {
		background: white;
		border: 1px solid #e2e8f0;
		padding: 0.5rem 0.75rem;
		border-radius: 0.5rem;
		cursor: pointer;
		font-size: 0.85rem;
		transition: all 0.2s;
	}

	.page-btn:hover:not(:disabled) {
		background: #e2e8f0;
	}

	.page-btn:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}

	.page-numbers {
		display: flex;
		gap: 0.25rem;
	}

	.page-num {
		min-width: 2rem;
		height: 2rem;
		display: flex;
		align-items: center;
		justify-content: center;
		background: white;
		border: 1px solid #e2e8f0;
		border-radius: 0.5rem;
		cursor: pointer;
		font-size: 0.85rem;
		transition: all 0.2s;
	}

	.page-num:hover:not(.dots):not(:disabled) {
		background: #e2e8f0;
	}

	.page-num.active {
		background: #667eea;
		border-color: #667eea;
		color: white;
	}

	.page-num.dots {
		cursor: default;
		border: none;
		background: transparent;
	}

	.page-info {
		color: white;
		font-size: 0.85rem;
		margin-left: 0.5rem;
	}

	/* 响应式 - 不同屏幕下调整列数 */
	@media (max-width: 1400px) {
		.software-grid {
			grid-template-columns: repeat(5, 1fr);
			gap: 1rem;
		}
	}

	@media (max-width: 1200px) {
		.software-grid {
			grid-template-columns: repeat(4, 1fr);
		}
	}

	@media (max-width: 900px) {
		.software-grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	@media (max-width: 600px) {
		.software-grid {
			grid-template-columns: repeat(2, 1fr);
		}
		
		.shared-software-page {
			padding: 1rem;
		}
	}

	@media (max-width: 400px) {
		.software-grid {
			grid-template-columns: 1fr;
		}
	}

	@media (max-width: 768px) {
		.pagination-wrapper {
			gap: 0.25rem;
		}

		.page-btn,
		.page-num {
			padding: 0.25rem 0.5rem;
			min-width: 1.75rem;
		}
	}
</style>